import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import ValidateEmail from '../components/EmailValidation'
import SideMenu from '../layouts/SideMenu'
import '../layouts/stylesheets/loader-style.css'
import { useLazyQuery } from '@apollo/client'
import {
  LOGIN_USER_QUERY,
  GET_USERDATA_QUERY,
  LOGIN_WITH_GOOGLE_QUERY,
} from '../services/GraphQLQueries'
import { setAuthToken, storeUserSession } from '../services/auth'
import { NavLink } from 'react-router-dom'

export default function SignIn() {
  const [loginUser, { loading }] = useMutation(LOGIN_USER_QUERY)
  const [googleLogin, { loading: googleLoading }] = useMutation(
    LOGIN_WITH_GOOGLE_QUERY
  )
  const [signinForm, setSigninForm] = useState({
    email: '',
    password: '',
  })
  const navigate = useNavigate()
  const [userDataQuery, { loadingUserData }] = useLazyQuery(GET_USERDATA_QUERY)
  const googleButtonRef = useRef(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formErrors, setFormErrors] = useState({ errors: {} })
  const isAuthBusy = loading || googleLoading || isSubmitting

  const handleChange = (event) => {
    const { id, value } = event.target
    setSigninForm({ ...signinForm, [id]: value })
    setFormErrors((currentErrors) => ({
      errors: {
        ...currentErrors.errors,
        [id]: '',
        google: '',
      },
    }))
  }

  const handlePostLogin = useCallback(async (bearerToken) => {
    setAuthToken(bearerToken)
    const userDataRes = await userDataQuery()
    const userData = userDataRes.data?.userdata || null
    const organizationData = userDataRes.data?.organization || null
    storeUserSession({ userData, organizationData })

    navigate('/trackers')
  }, [navigate, userDataQuery])

  const handleLogin = async (e) => {
    e.preventDefault()
    if (isAuthBusy) {
      return
    }

    let errors = {}
    let proceed = true
    if (signinForm.email.length < 3 || !ValidateEmail(signinForm.email)) {
      errors.email = 'Please enter a valid email address.'
      proceed = false
    }
    if (signinForm.password.length < 3) {
      errors.password = 'Please enter your password.'
      proceed = false
    }
    if (!proceed) {
      setFormErrors({ errors })
      return
    }

    setFormErrors({ errors: {} })
    setIsSubmitting(true)

    try {
      const res = await loginUser({
        variables: signinForm,
      })
      const bearerToken = res.data?.loginUser?.bearerToken

      if (!bearerToken) {
        setFormErrors({
          errors: {
            password:
              res.data?.loginUser?.error ||
              'Unable to sign in. Please try again.',
          },
        })
        return
      }

      await handlePostLogin(bearerToken)
    } catch (error) {
      setFormErrors({
        errors: {
          password: 'Unable to sign in. Please try again.',
        },
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleGoogleCredentialResponse = useCallback(async (response) => {
    if (isAuthBusy) {
      return
    }

    if (!response?.credential) {
      setFormErrors((currentErrors) => ({
        errors: {
          ...currentErrors.errors,
          google: 'Google sign-in was cancelled or failed to start.',
        },
      }))
      return
    }

    setFormErrors((currentErrors) => ({
      errors: {
        ...currentErrors.errors,
        google: '',
      },
    }))
    setIsSubmitting(true)

    try {
      const res = await googleLogin({
        variables: { credential: response.credential },
      })
      const bearerToken = res.data?.loginWithGoogle?.bearerToken

      if (bearerToken) {
        await handlePostLogin(bearerToken)
      } else {
        setFormErrors({
          errors: {
            google:
              res.data?.loginWithGoogle?.error ||
              'Unable to sign in with Google. Please try again.',
          },
        })
      }
    } catch (error) {
      setFormErrors({
        errors: {
          google: 'Unable to sign in with Google. Please try again.',
        },
      })
    } finally {
      setIsSubmitting(false)
    }
  }, [googleLogin, handlePostLogin, isAuthBusy])

  const initializeGoogleSignIn = useCallback(() => {
    if (!googleButtonRef.current) return
    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID || !window.google?.accounts?.id) {
      return
    }

    if (googleButtonRef.current.childElementCount > 0) return

    window.google.accounts.id.initialize({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      callback: handleGoogleCredentialResponse,
    })

    window.google.accounts.id.renderButton(googleButtonRef.current, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'continue_with',
      shape: 'rectangular',
      width: '320',
    })
  }, [handleGoogleCredentialResponse])

  useEffect(() => {
    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
      return
    }

    if (window.google?.accounts?.id) {
      initializeGoogleSignIn()
      return
    }

    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.defer = true
    script.onload = initializeGoogleSignIn
    document.body.appendChild(script)

    return () => {
      script.onload = null
    }
  }, [initializeGoogleSignIn])

  return (
    <div
      className="flex justify-center items-center min-h-screen bg-white"
      aria-live="polite"
    >
      <SideMenu hideBackArrow />
      <div className="w-full flex justify-center">
        {loadingUserData ? (
          <div className="loader-wrapper">
            <div className="flex justify-center items-center">
              <div className="grid gap-10">
                <div className="loader"></div>
                <div className="text-center font-unbounded">
                  Finishing sign in...
                </div>
              </div>
            </div>
          </div>
        ) : (
          <form
            onSubmit={handleLogin}
            className="border border-gray-200 rounded-lg shadow-sm p-6 w-[360px] space-y-4"
            autoComplete="on"
          >
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-black"
              >
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Email"
                value={signinForm.email}
                onChange={handleChange}
                disabled={isAuthBusy}
                autoComplete="username"
                className="mt-1 block w-full rounded-md border border-gray-300 p-2 text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              {formErrors.errors.email && (
                <p className="text-red-600 text-xs mt-1 text-center font-medium">
                  {formErrors.errors.email}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-black"
              >
                Password
              </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  placeholder="Password"
                  value={signinForm.password}
                  onChange={handleChange}
                  disabled={isAuthBusy}
                  className="mt-1 block w-full rounded-md border border-gray-300 p-2 text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  autoComplete="current-password"
                />
              {formErrors.errors.password && (
                <p className="text-red-600 text-xs mt-1 text-center font-medium">
                  {formErrors.errors.password}
                </p>
              )}
            </div>
            <button
              type="submit"
              disabled={isAuthBusy}
              className="w-full bg-black text-white py-2 rounded-md font-medium hover:bg-gray-900 transition disabled:cursor-not-allowed disabled:bg-gray-500"
            >
              {isAuthBusy ? 'Signing in...' : 'Sign In'}
            </button>
            <div className="text-sm text-left">
              <NavLink
                to="/reset-password"
                className="text-black underline hover:text-purple-600 "
              >
                Forgot password?
              </NavLink>
            </div>
            <div>
              <div
                ref={googleButtonRef}
                className={`flex justify-center ${
                  isAuthBusy ? 'pointer-events-none opacity-60' : ''
                }`}
              />
              {formErrors.errors.google && (
                <p className="text-red-600 text-xs mt-2 text-center font-medium">
                  {formErrors.errors.google}
                </p>
              )}
            </div>
            <div className="text-sm text-left text-gray-600">
              No account?{' '}
              <NavLink
                to="/"
                className="text-black underline hover:text-purple-600"
              >
                Create One
              </NavLink>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
